import chroma from "chroma-js";
import {Assert, E, GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {createRef} from "react";
import {UPlot} from "react-uplot";
import {Button, CheckBox, Column, DropDown, DropDownContent, DropDownTrigger, Pre, Row, Select, Spinner, Text} from "react-vcomponents";
import uPlot from "uplot";
import {Annotation} from "uplot-vplugins";
import useResizeObserver from "use-resize-observer";
import {AssertUnreachable, Chroma, ES, PageContainer, RunInAction_Set} from "web-vcore";
import {GetJournalEntries} from "../Server/DB/journalEntries.js";
import {GetSessions} from "../Server/DB/sessions.js";
import {GetMaps} from "../Server/DB/maps.js";
import {GetMapObjects} from "../Server/DB/mapObjects.js";
import {StatType_Label, StatsGrouping, StatsXType_entries, StatsYType, StatsYType_entries} from "../Server/DB/statViews/@StatTypes.js";
import {StatView} from "../Server/DB/statViews/@StatView.js";
import {MeID} from "../Server/DB/users.js";
import {store} from "../Store/index.js";
import {zIndexes} from "../Utils/UI/ZIndexes.js";
import {useExpandedNavBar} from "./@Shared/NavBar.js";
import {CreateAggregationMeta, GetYValuesForYType, JStatsUI_GetAggregationData} from "./Stats/AggregationData.js";
import {PopulateAnnotationsForChart} from "./Stats/AnnotationsPopulator.js";
import {ExtendedXTicksForSmoothing, JStatsUI_GetInitialTicks} from "./Stats/Helpers.js";
import {ALL_GROUP_NAME} from "./Stats/MetricCollector.js";
import {StatViewSettingsDropdown} from "./Stats/StatViewDetailsUI.js";
import {GetChartOptions} from "./Stats/StatsChartOptions.js";
import {JourneyStatsViewsDropdown} from "./Stats/ViewsDropdown.js";

export const StatsPage = observer_mgl(()=>{
	const uiState = store.main.stats;
	const largeVersion = useExpandedNavBar();

	return (
		<PageContainer preset="text" style={E(
			{padding: 0, height: "100%"},
			!largeVersion && {margin: "0 auto 0 auto"},
		)}>
			<JourneyStatsUI/>
		</PageContainer>
	);
});

/*function AddBaseMarker(lineTypes: uPlot.Series[], uplotData: uPlot.AlignedData, ticks: number[], uiState: JourneyStatsState) {
	const {yType} = uiState;

	const baseMarkerSeries = {
		label: "Baseline",
		stroke: "white",
		points: {show: false},
	} as uPlot.Series;
	let baseMarkerData: number[];
	baseMarkerData = ticks.map(x=>0);
	lineTypes.push(baseMarkerSeries);
	uplotData.push(new Float64Array(baseMarkerData));
}*/

export const JourneyStatsUI = observer_mgl(()=>{
	const {view} = store.main.stats;
	return (
		<Column style={{position: "relative", flex: 1}}>
			<Row style={{flexWrap: "wrap", gap: 5}}>
				<Row center>
					<Pre>X:</Pre>
					<Select ml={5} style={{fontSize: 12}} options={StatsXType_entries.map(a=>({name: StatType_Label(a), value: a}))} value={view.xType} onChange={val=>RunInAction_Set(()=>view.xType = val)}/>
				</Row>
				<Row center>
					<Pre>Y:</Pre>
					<Select ml={5} style={{fontSize: 12}} options={StatsYType_entries.map(a=>({name: StatType_Label(a), value: a}))} value={view.yType} onChange={val=>RunInAction_Set(()=>view.yType = val)}/>
				</Row>
				{view.yType == "combined" &&
				<CombinedYType_Dropdown/>}
				<Row center>
					<Pre>Group:</Pre>
					<Select ml={5} style={{fontSize: 12}} options={GetEntries(StatsGrouping, a=>StatType_Label(StatsGrouping[a]))} value={view.grouping} onChange={val=>RunInAction_Set(()=>view.grouping = val)}/>
				</Row>

				<Row ml="auto">
					<JourneyStatsViewsDropdown/>
					<StatViewSettingsDropdown/>
				</Row>
			</Row>
			<JourneyGraph/>
		</Column>
	);
});

const JourneyGraph = observer_mgl((props: {})=>{
	const rootRef = createRef<HTMLDivElement>();
	const chartRef = createRef<uPlot>();

	const {view} = store.main.stats;
	const {ref: uplotHolderRef, width = -1, height = -1} = useResizeObserver();
	if (view.xType == null) return <div>Invalid view x-type: null</div>;
	if (view.yType == null) return <div>Invalid view y-type: null</div>;
	if (view.combined_yTypes == null) return <div>Invalid view combined_yTypes: null</div>;
	if (view.grouping == null) return <div>Invalid view grouping: null</div>;
	if (view.dreamMatcher_text == null) return <div>Invalid view dreamMatcher_text: null</div>;
	if (view.dateRange_enabled == null) return <div>Invalid view dateRange_enabled: null</div>;
	//if (view.dateRange_min == null) return <div>Invalid view dateRange_min: null</div>;
	//if (view.dateRange_max == null) return <div>Invalid view dateRange_max: null</div>;
	if (view.combined_yTypes_scales == null) return <div>Invalid view combined_yTypes_scales: null</div>;
	//if (view.groupRenames == null) return <div>Invalid view groupRenames: null</div>;

	// commented; this system is bad, because it relies on this.chart.current, which is null until the *second* complete render (so if nothing retriggers a re-render, render logic depending on it can't work)
	/*const [rootRect, setRootRect] = UseState<VRect|n>(null);
	const [chartBodyRect, setChartBodyRect] = UseState<VRect|n>(null);
	UseEffect(()=>{
		// after each render, find the chart-body subrect, and store it in state (so rating-markers can adjust to it)
		const newRootRect = this.DOM_HTML ? GetPageRect(this.DOM_HTML) : null;
		const newChartBodyRect = this.chart.current?.under ? GetViewportRect(this.chart.current?.under) : null;
		if (newRootRect && newRootRect.width && newChartBodyRect && newChartBodyRect.width) {
			if (!newRootRect.Equals(rootRect)) setRootRect(newRootRect);
			if (!newChartBodyRect.Equals(chartBodyRect)) setChartBodyRect(newChartBodyRect);
		}
	});
	const rectsResolved = rootRect != null && chartBodyRect != null;*/

	const series: uPlot.Series[] = [
		{
			label: view.xAxis_labelOverride || StatType_Label(view.xType),
			scale: "x",
		} as any,
	];
	const xTicks_initial = JStatsUI_GetInitialTicks(view);

	const sessions = GetSessions(MeID());
	const dreams = GetJournalEntries(MeID());
	const meta = CreateAggregationMeta(
		view.xType, view.yType, view.combined_yTypes, view.grouping, view.dreamMatcher_text, view.maxMemoryPromptRecallGap, view.lucidityLevels,
		view.dateRange_enabled, view.dateRange_min, view.dateRange_max, view.xAxis_stepOverride_enabled ? view.xAxis_stepOverride : null,
	);
	const maps = GetMaps();
	const mapObjects_all = maps?.flatMap(map=>GetMapObjects(map.id)) ?? [];
	const aggregationData = JStatsUI_GetAggregationData(sessions, dreams, mapObjects_all, meta, xTicks_initial, view.groupRenames ?? []);
	if (window["log_aggregationData"]) console.log("Aggregation data:", aggregationData);

	// apply the x-axis max-override, at the data level (the chartOptions.scales.x.range fails when the "bars" plugin/view-type is active)
	// note: this approach is arguably not 100% "correct", because smoothing happens "after" this trimming, eg. affecting smoothed-value at right-edge (if smooth-type is "centered")
	for (let i = 0; i < aggregationData.xTicks.length; i++) {
		const xTick = aggregationData.xTicks[i];
		// if we reach an x-tick that is higher than the max, then truncate all series to that point
		if (view.xAxis_maxOverride_enabled && view.xAxis_maxOverride != null && xTick > view.xAxis_maxOverride) {
			aggregationData.xTicks = aggregationData.xTicks.slice(0, i); // truncate the x-ticks to the max
			/*for (let i2 = 0; i2 < uplotData.length; i2++) {
				uplotData[i2] = uplotData[i2].slice(0, i); // truncate all series to the max
			}*/
			break;
		}
	}

	const xTicks_extendedForSmoothing = ExtendedXTicksForSmoothing(aggregationData.xTicks, view, StatView.WillApplySmoothing(view));

	const uplotData = [aggregationData.xTicks] as uPlot.AlignedData;
	//AddBaseMarker(series, uplotData, xValues, uiState);

	let groups = [] as string[];
	let groupScales = new Map<string, string>();
	if (view.yType == "combined") {
		groups = view.combined_yTypes as string[];
		groupScales = groups.ToMap(a=>a, a=>view.combined_yTypes_scales![a] ? `y_${view.combined_yTypes_scales![a]}` : "y");
	} else {
		if (view.grouping == StatsGrouping.none) {
			groups.push(ALL_GROUP_NAME);
		} else if (view.grouping == StatsGrouping.variant) {
			groups.push(...aggregationData.groups_variants);
		} else if (view.grouping == StatsGrouping.alarmSequence) {
			groups.push(...aggregationData.groups_alarmSequences);
		} else if (view.grouping == StatsGrouping.alarmDelay) {
			groups.push(...aggregationData.groups_alarmDelays);
		} else if (view.grouping == StatsGrouping.volumeMod) {
			groups.push(...aggregationData.groups_globalVolumeMods);
		} else if (view.grouping == StatsGrouping.alarmRestartIntervalMod) {
			groups.push(...aggregationData.groups_globalAlarmRestartIntervalMods);
		} else if (view.grouping == StatsGrouping.memoryPalaceTarget) {
			groups.push(...aggregationData.groups_memoryPalaceTargets);
		} else {
			AssertUnreachable(view.grouping);
		}
		// if there's a group named "" (ie. an empty string), the user must have renamed an old/unwanted group to that; as per the UI tooltip, hide/exclude this group
		groups = groups.filter(a=>a != "");
		groups = groups.OrderBy(a=>a);
		groupScales = groups.ToMap(a=>a, a=>"y");
	}

	const hueShiftPerGroup = 360 / groups.length;
	const yValuesPerGroup = new Map<string, number[]>();
	for (const [index, group] of groups.entries()) {
		const strokeColor = chroma(hueShiftPerGroup * index, 1, .5, "hsl");
		series.push(E(
			{
				label: StatsYType_entries.includes(group as any) ? StatType_Label(group as any) : group,
				scale: groupScales.get(group),
				stroke: strokeColor.css(),
				fill: view.renderType == "bars"
					? strokeColor.css()
					: strokeColor.alpha(.1).css(),
				points: {show: false},
				// stepped-mode
				//paths: paths_stepped,
				paths:
					view.renderType == "bars" ? uPlot.paths.bars!() :
					//uiState.renderType == "bars" ? uPlot.paths.bars!({align: -1}) :
					view.renderType == "spline" ? uPlot.paths.spline!() :
					view.renderType == "linear" ? uPlot.paths.linear!() :
					view.renderType == "stepped" ? uPlot.paths.stepped!({align: -1}) :
					Assert(false, `Invalid render-type: ${view.renderType}`),
			} as uPlot.Series,
			groups.length == 1 && {
				stroke: Chroma("hsla(210,30%,90%,1)").alpha(.4).css(),
				fill: Chroma("hsla(210,30%,90%,1)").alpha(.3).css(),
			},
		));
		const yTypeHere = view.yType == "combined" ? group as StatsYType : view.yType;
		const groupHere = view.yType == "combined" ? ALL_GROUP_NAME : group;
		const yValues = GetYValuesForYType(view, aggregationData.xTicks, xTicks_extendedForSmoothing, aggregationData, yTypeHere, groupHere);
		const yValues_nullsAs0 = yValues.map(a=>a ?? 0); // no need to preserve nulls after smoothing has been applied
		uplotData.push(new Float64Array(yValues_nullsAs0)); // array stores the y-values at each x-pos/session-number, for this scheme's line/series
		yValuesPerGroup.set(group, yValues_nullsAs0);
	}

	let chartOptions: uPlot.Options|n;
	const annotations = [] as Annotation[];
	if (groups.length) {
		chartOptions = GetChartOptions({width, height, series, annotations, xValues: aggregationData.xTicks, aggregationData});
		PopulateAnnotationsForChart({width, height, chartOptions, annotations, xValues: aggregationData.xTicks, groups, yValuesPerGroup, aggregationData});
	}

	return (
		<div ref={rootRef}
			style={ES(
				//{position: "relative"}, //minWidth: 496
				//asNodeUIOverlay && {position: "absolute", left: 0, right: 0, top: 0, bottom: 0, pointerEvents: "none"},
				//{position: "absolute", left: 0, right: 0, top: 30, bottom: 0, pointerEvents: "none"},
				{position: "relative", flex: 1},
			)}>

			{chartOptions != null &&
			<div ref={uplotHolderRef as any} className="uplotHolder" style={ES({
				position: "relative", width: "100%",
				//height: "calc(100% - 53px)", // we need to cut off some height, for the legend
				height: "100%",
			})}>
				{width != -1 &&
				<>
					<style>{`
					.u-legend { font-size: 12px; }
					.u-legend .hideLegend { display: none; }
					`}</style>
					<UPlot chartRef={chartRef} options={chartOptions} data={uplotData} ignoreDoubleClick={true}/>
				</>}
			</div>}
			{chartOptions == null &&
			<Text>No data to display.</Text>}
		</div>
	);
});

export const CombinedYType_Dropdown = observer_mgl(()=>{
	const {view} = store.main.stats;
	if (view.combined_yTypes == null || view.combined_yTypes_scales == null) return <div>Some view properties required are null</div>;

	const options = StatsYType_entries.map(a=>{
		return {
			name: StatType_Label(a),
			value: a,
		};
	});

	return (
		<DropDown style={{marginLeft: 5}}>
			<DropDownTrigger>
				<Button text="Combined"/>
			</DropDownTrigger>
			<DropDownContent style={{width: 300, zIndex: zIndexes.dropDown, background: "rgba(0,0,0,.9)", borderRadius: 5}}>
				<Row>
					<Text>Y-type (enabled), scale max (0 for auto)</Text>
				</Row>
				{options.map(opt=>{
					return (
						<Row key={opt.value}>
							<CheckBox text={opt.name} value={view.combined_yTypes!.includes(opt.value)} onChange={val=>{
								RunInAction_Set(()=>{
									view.combined_yTypes = options.filter(a=>{
										return a.value == opt.value ? val : view.combined_yTypes!.includes(a.value);
									}).map(a=>a.value);
								});
							}}/>
							<Spinner ml={5} value={view.combined_yTypes_scales![opt.value] ?? 0} onChange={val=>{
								RunInAction_Set(()=>{
									if (val > 0) view.combined_yTypes_scales![opt.value] = val;
									else delete view.combined_yTypes_scales![opt.value];
								});
							}}/>
						</Row>
					);
				})}
			</DropDownContent>
		</DropDown>
	);
});